import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../../components/Layout";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
    TrendUp, MapPin, FileText, Robot, Ruler, CurrencyEur,
    ArrowClockwise, CalendarBlank, ArrowUp, ArrowDown, Minus,
} from "@phosphor-icons/react";

const TONE = {
    strong: { cls: "bg-emerald-50 border-emerald-300 text-emerald-800", dot: "bg-emerald-500" },
    moderate: { cls: "bg-blue-50 border-blue-300 text-blue-800", dot: "bg-blue-500" },
    neutral: { cls: "bg-gray-50 border-gray-300 text-gray-700", dot: "bg-gray-400" },
    bad: { cls: "bg-rose-50 border-rose-300 text-rose-800", dot: "bg-rose-500" },
};

const fmt = (v) => (v == null ? "—" : Number(v).toLocaleString("it-IT"));

function Delta({ abs, pct }) {
    if (abs == null) return <span className="text-gray-400 text-sm">nessuna baseline</span>;
    const up = abs > 0, flat = abs === 0;
    const Icon = flat ? Minus : up ? ArrowUp : ArrowDown;
    const color = flat ? "text-gray-500" : up ? "text-emerald-600" : "text-rose-600";
    return (
        <span className={`inline-flex items-center gap-1 text-sm font-semibold ${color}`}>
            <Icon size={14} weight="bold" />
            {abs > 0 ? "+" : ""}{fmt(abs)}
            {pct != null && <span className="opacity-70">({pct > 0 ? "+" : ""}{pct}%)</span>}
        </span>
    );
}

function MetricCard({ icon: Icon, title, m, testid }) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl shadow-card p-6" data-testid={testid}>
            <div className="flex items-center gap-2 mb-5">
                <Icon size={18} weight="fill" className="text-gray-700" />
                <h3 className="font-display-black text-base">{title}</h3>
            </div>
            <div className="flex items-end justify-between gap-4">
                <div>
                    <div className="overline mb-1">Baseline</div>
                    <div className="font-mono-tight text-xl text-gray-400">{fmt(m?.baseline)}</div>
                </div>
                <div className="text-right">
                    <div className="overline mb-1">Attuale</div>
                    <div className="font-mono-tight text-4xl font-bold leading-none">{fmt(m?.current)}</div>
                </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
                <Delta abs={m?.delta_abs} pct={m?.delta_pct} />
            </div>
        </div>
    );
}

export default function ObservationGrowth() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [baselineId, setBaselineId] = useState("");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async (bid) => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/observation/growth", {
                params: bid ? { baseline_id: bid } : {},
            });
            setData(data);
            if (!bid && data.baseline?.id) setBaselineId(data.baseline.id);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

    const byKey = (k) => (data?.metrics || []).find((m) => m.key === k);
    const overall = data?.overall;
    const tone = TONE[overall?.tone] || TONE.neutral;
    const province = byKey("provinces_covered");
    const regioni = byKey("regions_covered");

    return (
        <Layout>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8" data-testid="obs-header">
                <div>
                    <div className="overline mb-2">Periodo di osservazione</div>
                    <h1 className="font-display-black text-4xl sm:text-5xl leading-none">Osservazione Crescita</h1>
                    <p className="text-sm text-gray-600 mt-3 max-w-xl">
                        Il sistema sta diventando più utile rispetto alla baseline?
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {(data?.snapshots_available?.length || 0) > 0 && (
                        <select
                            data-testid="obs-baseline-select"
                            value={baselineId}
                            onChange={(e) => { setBaselineId(e.target.value); load(e.target.value); }}
                            className="h-11 px-3 border border-gray-300 rounded-lg text-sm bg-white max-w-[260px] truncate hover:border-black transition-colors"
                        >
                            {data.snapshots_available.map((s) => (
                                <option key={s.id} value={s.id}>{s.label}</option>
                            ))}
                        </select>
                    )}
                    <button
                        data-testid="obs-refresh-btn"
                        onClick={() => load(baselineId)}
                        className="h-11 px-4 inline-flex items-center gap-2 border border-gray-300 hover:border-black rounded-lg text-sm font-semibold transition-colors"
                    >
                        <ArrowClockwise size={15} weight="bold" /> Aggiorna
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-gray-500 py-16 text-center" data-testid="obs-loading">Caricamento…</div>
            ) : !data?.baseline ? (
                <div className="border border-dashed border-gray-300 rounded-xl p-10 text-center text-sm text-gray-500" data-testid="obs-no-baseline">
                    Nessuna baseline catturata. Cattura una baseline KPI per iniziare l&apos;osservazione.
                </div>
            ) : (
                <>
                    {/* Top row: days + overall status */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
                        <div className="bg-white border border-gray-200 rounded-xl shadow-card p-6 flex items-center gap-4" data-testid="obs-days">
                            <div className="w-12 h-12 rounded-lg bg-gray-900 text-white flex items-center justify-center shrink-0">
                                <CalendarBlank size={22} weight="fill" />
                            </div>
                            <div>
                                <div className="overline mb-1">Giorni trascorsi</div>
                                <div className="font-mono-tight text-3xl font-bold leading-none">
                                    Giorno {data.day_number}<span className="text-gray-400 text-xl">/{data.period_days}</span>
                                </div>
                                <div className="text-xs text-gray-500 mt-1.5">
                                    Baseline: {data.baseline.label}
                                </div>
                            </div>
                        </div>

                        <div className={`lg:col-span-2 border rounded-xl shadow-card p-6 flex items-center justify-between gap-4 ${tone.cls}`} data-testid="obs-overall">
                            <div>
                                <div className="overline mb-1 opacity-80">Stato generale</div>
                                <div className="flex items-center gap-2.5">
                                    <span className={`w-3 h-3 rounded-full ${tone.dot}`} />
                                    <span className="font-display-black text-2xl sm:text-3xl leading-none">{overall.status}</span>
                                </div>
                            </div>
                            <div className="text-right text-sm shrink-0">
                                <div><span className="font-bold">{overall.improved}</span> in crescita</div>
                                <div><span className="font-bold">{overall.regressed}</span> in calo</div>
                                <div className="opacity-80">media {overall.avg_growth_pct > 0 ? "+" : ""}{overall.avg_growth_pct}%</div>
                            </div>
                        </div>
                    </div>

                    {/* Metric grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        <MetricCard icon={TrendUp} title="Crescita inventario" m={byKey("distinct_total")} testid="obs-metric-inventory" />

                        {/* Geographic coverage groups province + regioni */}
                        <div className="bg-white border border-gray-200 rounded-xl shadow-card p-6" data-testid="obs-metric-geo">
                            <div className="flex items-center gap-2 mb-5">
                                <MapPin size={18} weight="fill" className="text-gray-700" />
                                <h3 className="font-display-black text-base">Copertura geografica</h3>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <div className="overline mb-1">Province</div>
                                    <div className="font-mono-tight text-3xl font-bold leading-none">{fmt(province?.current)}</div>
                                    <div className="text-xs text-gray-400 mt-1">da {fmt(province?.baseline)}</div>
                                    <div className="mt-1"><Delta abs={province?.delta_abs} pct={province?.delta_pct} /></div>
                                </div>
                                <div>
                                    <div className="overline mb-1">Regioni</div>
                                    <div className="font-mono-tight text-3xl font-bold leading-none">{fmt(regioni?.current)}</div>
                                    <div className="text-xs text-gray-400 mt-1">da {fmt(regioni?.baseline)}</div>
                                    <div className="mt-1"><Delta abs={regioni?.delta_abs} pct={regioni?.delta_pct} /></div>
                                </div>
                            </div>
                        </div>

                        <MetricCard icon={FileText} title="Perizie" m={byKey("perizie_found")} testid="obs-metric-perizie" />
                        <MetricCard icon={Robot} title="Deep Ready" m={byKey("deep_ready")} testid="obs-metric-deepready" />
                        <MetricCard icon={Ruler} title="Superficie disponibile" m={byKey("surface_available")} testid="obs-metric-surface" />
                        <MetricCard icon={CurrencyEur} title="€/m² disponibile" m={byKey("price_sqm_available")} testid="obs-metric-pricesqm" />
                    </div>
                </>
            )}
        </Layout>
    );
}
